<script setup lang="ts">
/**
 * 额度条。顶部导航和额度页共用。
 *
 * `quota === null` 时**什么都不渲染**：一个空进度条会被读成"额度为 0"，
 * 而真相只是"还没拿到数据"。
 *
 * v1.3.0 起这里只显示服务端算好的**剩余百分比**：绝对用量已经不再下发，
 * 所以旧版那套 M/k 缩写连同"已用 X / 剩余 X / Y"这类文案一起删掉了——
 * 没有数字可格式化，就不会再有"顺手多显示一点"的余地。
 *
 * 不限额度说「不限」而不是 100%：一个永远满格的进度条会让"不设上限"看起来像"快满了"。
 * 重置时刻这一行显示的是**钟点**而不是倒计时：额度条常驻顶栏，没有自己的计时器，
 * 一句"3 小时后重置"会一直停在它诞生那一刻（额度页的计量条才有 `now` 可以走）。
 */

import { computed } from 'vue';
import type { Quota } from '@spirit/contracts';

import { cn } from '../../lib/cn';
import {
  UNLIMITED_QUOTA_HINT,
  quotaScopeTitleOf,
  quotaViewOf,
} from '../../lib/quota-display';

const props = withDefaults(
  defineProps<{ quota: Quota | null; className?: string; compact?: boolean }>(),
  { compact: false },
);

function formatResetAt(resetAt: string): string {
  const date = new Date(resetAt);
  if (Number.isNaN(date.getTime())) return '—';
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
}

const view = computed(() => (props.quota === null ? null : quotaViewOf(props.quota)));

/** 不限额度时没有"还剩多少"可谈，重置时间也没有意义，只说清状态。 */
const unlimitedHint = computed(() => {
  const current = view.value;
  return current !== null && current.kind === 'unlimited' ? UNLIMITED_QUOTA_HINT : '';
});
</script>

<template>
  <template v-if="view !== null">
    <div
      v-if="view.kind === 'unlimited'"
      :class="
        cn(
          'flex items-center gap-3 border-t px-4 text-xs',
          props.compact ? 'py-1.5' : 'py-2',
          'border-brand-100 bg-brand-50 text-brand-900',
          props.className,
        )
      "
    >
      <span class="shrink-0">额度</span>
      <span class="font-medium text-brand-700">不限</span>
      <span class="ml-auto hidden shrink-0 text-slate-500 sm:inline">{{ unlimitedHint }}</span>
      <!-- 给"套餐档次徽章"留的位（Header 用它把档位钉在额度条里）。 -->
      <slot name="trailing" />
    </div>

    <div
      v-else
      :class="
        cn(
          'flex items-center gap-3 border-t px-4 text-xs',
          props.compact ? 'py-1.5' : 'py-2',
          view.low ? 'border-red-200 bg-red-50 text-red-800' : 'border-brand-100 bg-brand-50 text-brand-900',
          props.className,
        )
      "
    >
      <span class="shrink-0">{{ quotaScopeTitleOf(view.scope) }}</span>
      <div
        :class="cn('h-1.5 min-w-[60px] flex-1 overflow-hidden rounded-full', view.low ? 'bg-red-200' : 'bg-brand-200')"
        role="progressbar"
        :aria-label="`${quotaScopeTitleOf(view.scope)}剩余百分比`"
        :aria-valuemin="0"
        :aria-valuemax="100"
        :aria-valuenow="view.remainingPercent"
        :aria-valuetext="`剩余 ${view.remainingPercent}%`"
      >
        <div
          :class="cn('h-full rounded-full transition-all', view.low ? 'bg-red-500' : 'bg-brand-500')"
          :style="{ width: `${view.remainingPercent}%` }"
        />
      </div>
      <span class="shrink-0 font-medium tabular-nums">剩余 {{ view.remainingPercent }}%</span>
      <span class="hidden shrink-0 tabular-nums text-slate-500 sm:inline">
        {{ formatResetAt(view.resetAt) }} 重置
      </span>
      <span v-if="view.low" class="hidden shrink-0 font-medium sm:inline" role="status">
        额度偏低
      </span>
      <!-- 给"套餐档次徽章"留的位（Header 用它把档位钉在额度条里）。 -->
      <slot name="trailing" />
    </div>
  </template>
</template>
